import { ProtocolSettings } from "./ProtocolSettings";
import { ProtocolSettingsTypes } from "./ProtocolSettingsTypes";
import { ObjUtils } from "../Utils/ObjUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { StringUtils } from "../Utils";

export enum SnmpVersion {
	Ver1 = 0,
	Ver2 = 1,
	Ver3 = 3
}

export class SnmpSettings extends ProtocolSettings {

	public static readonly SnmpPortDefault: number = 161;

	public Version: SnmpVersion;

	public Port: number;

	public ReadCommunity: string;
	public WriteCommunity: string;

	public Timeout: number;
	public Retries: number;

	public Username: string;
	public get HasUsername(): boolean {
		return !!this.Username;
	}

	public Context: string;
	public get HasContext(): boolean {
		return !!this.Context;
	}

	public AuthProtocol: string;
	public AuthPassword: string;

	public EncryptProtocol: string;
	public EncryptPassword: string;

	public UseGetBulk: boolean;
	public DisableReplySourceCheck: boolean;
	public IgnoreEngineBoots: boolean;

	constructor() {
		super();

		this.Version = SnmpVersion.Ver1;

		this.Port = SnmpSettings.SnmpPortDefault;

		this.ReadCommunity = "public";
		this.WriteCommunity = "";

		this.Timeout = 2000;
		this.Retries = 1;

		// SNMPv3 settings
		this.Username = "";
		this.Context = "";

		this.AuthProtocol = "";
		this.AuthPassword = "";

		this.EncryptProtocol = "";
		this.EncryptPassword = "";

		this.UseGetBulk = false;
		this.DisableReplySourceCheck = false;
		this.IgnoreEngineBoots = false;
	}

	public get SettingsType(): string {
		return ProtocolSettingsTypes.Snmp;
	}

	public get Summary(): string {
		if (this.Version === SnmpVersion.Ver3) {
			return `Version: ${this.Version}; Username: ${this.Username}; Context: ${this.Context}; Authentication Protocol: ${this.AuthProtocol}; Encryption Protocol: ${this.EncryptProtocol}`;
		}
		else {
			let hiddenWriteCommunity = "";
			if (this.WriteCommunity) {
				let clarifiedWriteCommunity = StringUtils.ClarifyString2(this.WriteCommunity);
				hiddenWriteCommunity = StringUtils.Repeat("-", clarifiedWriteCommunity.length);
			}
			return `Version: ${this.Version}; Read Community: ${this.ReadCommunity}; Write Community: ${hiddenWriteCommunity}; Timeout: ${this.Timeout}; Retries: ${this.Retries}`;
		}
	}

	public Copy(obj: ProtocolSettings): void {
		if (!(obj instanceof SnmpSettings))
			return;
		let other = (obj as SnmpSettings);

		super.Copy(obj);

		this.Port = other.Port;
		this.ReadCommunity = other.ReadCommunity;
		this.WriteCommunity = other.WriteCommunity;
		this.Timeout = other.Timeout;
		this.Retries = other.Retries;
		this.Version = other.Version;

		// SNMPv3
		this.Username = other.Username;
		this.Context = other.Context;

		this.AuthProtocol = other.AuthProtocol;
		this.AuthPassword = other.AuthPassword;

		this.EncryptProtocol = other.EncryptProtocol;
		this.EncryptPassword = other.EncryptPassword;

		this.UseGetBulk = other.UseGetBulk;
		this.DisableReplySourceCheck = other.DisableReplySourceCheck;
		this.IgnoreEngineBoots = other.IgnoreEngineBoots;
	}

	public Clone(): ProtocolSettings {
		let clone = new SnmpSettings();
		clone.Copy(this);
		return clone;
	}

	public Equals(obj: any): boolean {
		if (!super.Equals(obj))
			return false;

		if (!(obj instanceof SnmpSettings))
			return false;
		let other: SnmpSettings = (obj as SnmpSettings);

		return (this.Version === other.Version &&
			this.Port === other.Port &&
			ObjUtils.Equals(this.ReadCommunity, other.ReadCommunity) &&
			ObjUtils.Equals(this.WriteCommunity, other.WriteCommunity) &&
			this.Timeout === other.Timeout &&
			this.Retries === other.Retries &&
			ObjUtils.Equals(this.Username, other.Username) &&
			ObjUtils.Equals(this.Context, other.Context) &&
			ObjUtils.Equals(this.AuthProtocol, other.AuthProtocol) &&
			ObjUtils.Equals(this.AuthPassword, other.AuthPassword) &&
			ObjUtils.Equals(this.EncryptProtocol, other.EncryptProtocol) &&
			ObjUtils.Equals(this.EncryptPassword, other.EncryptPassword) &&
			(this.UseGetBulk === other.UseGetBulk) &&
			(this.IgnoreEngineBoots === other.IgnoreEngineBoots) &&
			(this.DisableReplySourceCheck == other.DisableReplySourceCheck));
	}

	public Write(obj: any, context: any): void {
		obj.snmp = {};
		obj = obj.snmp;

		super.Write(obj, context);

		obj.version = SnmpVersion[this.Version];
		obj.port = ValueEncoder.EncodeInteger(this.Port);
		obj.read_community = this.ReadCommunity;
		obj.write_community = this.WriteCommunity;
		obj.timeout = ValueEncoder.EncodeInteger(this.Timeout);
		obj.retries = ValueEncoder.EncodeInteger(this.Retries);

		obj.user_name = this.Username;
		obj.context = this.Context;

		obj.auth_protocol = this.AuthProtocol;
		obj.auth_password = this.AuthPassword;

		obj.encrypt_protocol = this.EncryptProtocol;
		obj.encrypt_password = this.EncryptPassword;

		obj.use_getbulk = ValueEncoder.EncodeBoolean(this.UseGetBulk);
		obj.ignore_snmpv3_engineboots = ValueEncoder.EncodeBoolean(this.IgnoreEngineBoots);
		obj.disable_reply_src_check = ValueEncoder.EncodeBoolean(this.DisableReplySourceCheck);
	}

	public Read(obj: any, context: any): void {
		if (!obj.snmp)
			throw new Error();
		obj = obj.snmp;

		super.Read(obj, context);

		if (obj.version !== undefined)
			this.Version = <SnmpVersion>SnmpVersion[<string>obj.version];

		if (obj.port !== undefined)
			this.Port = ValueEncoder.DecodeInteger(obj.port);

		if (obj.read_community !== undefined)
			this.ReadCommunity = obj.read_community;

		if (obj.write_community !== undefined)
			this.WriteCommunity = obj.write_community;

		if (obj.timeout !== undefined)
			this.Timeout = ValueEncoder.DecodeInteger(obj.timeout);

		if (obj.retries !== undefined)
			this.Retries = ValueEncoder.DecodeInteger(obj.retries);

		if (obj.user_name !== undefined)
			this.Username = obj.user_name;

		if (obj.context !== undefined)
			this.Context = obj.context;

		if (obj.auth_protocol !== undefined)
			this.AuthProtocol = obj.auth_protocol;

		if (obj.auth_password !== undefined)
			this.AuthPassword = obj.auth_password;

		if (obj.encrypt_protocol !== undefined)
			this.EncryptProtocol = obj.encrypt_protocol;

		if (obj.encrypt_password !== undefined)
			this.EncryptPassword = obj.encrypt_password;

		if (obj.use_getbulk !== undefined)
			this.UseGetBulk = ValueEncoder.DecodeBoolean(obj.use_getbulk);
		if (obj.ignore_snmpv3_engineboots !== undefined)
			this.IgnoreEngineBoots = ValueEncoder.DecodeBoolean(obj.ignore_snmpv3_engineboots);

		if (obj.disable_reply_src_check !== undefined)
			this.DisableReplySourceCheck = ValueEncoder.DecodeBoolean(obj.disable_reply_src_check);

	}

}
